import { useState } from "react";
import { CheckCircle2, MessageCircle } from "lucide-react";

import { ApiError } from "@/lib/api-client";
import type { OtpResult, SignInput } from "@/lib/orders";
import { SignaturePad } from "./SignaturePad";
import { BTN_GHOST, BTN_GOLD, BTN_GREEN } from "./ui";

/**
 * Code + signature, shared by the customer's page and the on-site review:
 * request the code, type it, sign, accept, submit.
 */
export function SignFlow({
  defaultName,
  hasEmail,
  requestOtp,
  sign,
  onSigned,
  startLabel = "Send me a code on WhatsApp",
}: {
  defaultName: string;
  hasEmail: boolean;
  requestOtp: (channel: "whatsapp" | "email") => Promise<OtpResult>;
  sign: (dto: SignInput) => Promise<{ ok: true; signedPdfUrl: string }>;
  onSigned: (signedPdfUrl: string) => void;
  startLabel?: string;
}) {
  const [otp, setOtp] = useState<OtpResult | null>(null);
  const [code, setCode] = useState("");
  const [name, setName] = useState(defaultName);
  const [accepted, setAccepted] = useState(false);
  const [png, setPng] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const ask = async (channel: "whatsapp" | "email") => {
    setBusy("otp");
    setError(null);
    try {
      const r = await requestOtp(channel);
      setOtp(r);
      if (r.result !== "sent") setError(`The code could not be delivered (${r.result}). Ask the Versus team for help.`);
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(null);
    }
  };

  const submit = async () => {
    if (!png || !accepted || code.trim().length < 4 || name.trim().length < 2) return;
    setBusy("sign");
    setError(null);
    try {
      const r = await sign({ code: code.trim(), signerName: name.trim(), accepted: true, signaturePng: png });
      onSigned(r.signedPdfUrl);
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-5">
      <div className="glass-panel-strong p-4">
        <div className="brand-kicker">Step 1 · one-time code</div>
        {otp ? (
          <p className="mt-2 text-sm text-paper/90">
            Code sent on {otp.channel} to <b dir="ltr">{otp.sentTo}</b>. It expires in 10 minutes.{" "}
            <button type="button" className="text-pink underline underline-offset-2" disabled={busy !== null} onClick={() => ask(otp.channel as "whatsapp" | "email")}>
              Send again
            </button>
          </p>
        ) : (
          <div className="mt-3 flex flex-wrap gap-2">
            <button type="button" className={BTN_GREEN} disabled={busy !== null} onClick={() => ask("whatsapp")}>
              <MessageCircle className="h-4 w-4" /> {busy === "otp" ? "Sending…" : startLabel}
            </button>
            {hasEmail && (
              <button type="button" className={BTN_GHOST} disabled={busy !== null} onClick={() => ask("email")}>
                Send by email instead
              </button>
            )}
          </div>
        )}
        {otp && (
          <label className="mt-3 block">
            <span className="field-label">Enter the 6-digit code</span>
            <input
              className="field num text-center text-2xl tracking-[0.5em]"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              dir="ltr"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            />
          </label>
        )}
      </div>

      <div className={`glass-panel-strong p-4 ${otp ? "" : "opacity-50"}`}>
        <div className="brand-kicker">Step 2 · signature</div>
        <label className="mt-3 block">
          <span className="field-label">Your full name</span>
          <input className="field" value={name} disabled={!otp} onChange={(e) => setName(e.target.value)} />
        </label>
        <div className="mt-3">
          <SignaturePad onChange={setPng} disabled={!otp} />
        </div>
        <label className="mt-3 flex items-start gap-3 text-sm text-paper/90">
          <input type="checkbox" className="mt-1 h-4 w-4 accent-[#C8A44E]" disabled={!otp} checked={accepted} onChange={(e) => setAccepted(e.target.checked)} />
          <span>I accept this order, its prices and terms, and I agree to sign it electronically.</span>
        </label>
      </div>

      {error && <p role="alert" className="rounded-lg border border-rose/60 bg-rose/15 px-3 py-2 text-sm text-pink">{error}</p>}

      <button
        type="button"
        className={`${BTN_GOLD} w-full py-3 text-sm`}
        disabled={busy !== null || !otp || !png || !accepted || code.length < 6 || name.trim().length < 2}
        onClick={submit}
      >
        <CheckCircle2 className="h-4 w-4" /> {busy === "sign" ? "Signing…" : "Sign the order"}
      </button>
    </div>
  );
}

function message(e: unknown): string {
  if (!(e instanceof ApiError)) return "Something went wrong. Please try again.";
  try {
    const parsed = JSON.parse(e.message) as { message?: string | string[] };
    const m = Array.isArray(parsed.message) ? parsed.message.join(", ") : parsed.message;
    if (m) return m;
  } catch {
    // not JSON
  }
  return e.status === 429 ? "Too many attempts. Please wait a little and try again." : "Something went wrong. Please try again.";
}
